import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Check, ReportProblemOutlined } from '@mui/icons-material'
import { Box, FormControlLabel, Switch, Theme, Typography, alpha } from '@mui/material'

import { TrackedGameBlueprint } from '../../../../../features/live-events/types/TrackedGame'
import { BlueprintFamily } from '../../../../api/markets'
import { languageServiceNameResolver } from '../../../../components/BlueprintFamilySearch/blueprintFamilyGrouping'
import { gameBlueprintSelectableForEventType } from '../../helpers/acceptedEventTypes'
import { useLiveEventsTrackingUserLanguage } from '../../hooks/useLiveEventsTrackingUserLanguage'

// Status surfaces, as tints of the theme palette.
const ruleColor = (theme: Theme) => theme.palette.background.default
const mutedText = (theme: Theme) => alpha(theme.palette.text.primary, 0.75)
const oddCaseBackground = (theme: Theme) => alpha(theme.palette.warning.main, 0.05)
const oddCaseBadgeBackground = (theme: Theme) => alpha(theme.palette.warning.main, 0.13)
const acceptedBackground = (theme: Theme) => alpha(theme.palette.success.main, 0.05)
const acceptedBadgeBackground = (theme: Theme) => alpha(theme.palette.success.main, 0.14)

type EventOddCaseStatusFooterProps = {
  /** The game blueprint the event is linked to, if any. */
  gameBlueprint?: TrackedGameBlueprint
  eventTypeId?: string
  blueprintFamilies?: BlueprintFamily[]
  /** The event's typeAccepted. */
  accepted: boolean
  onAcceptedChange: (accepted: boolean) => void
}

/**
 * The last row of the event card while the event is an odd case: its type is not one the Blueprint
 * Family of its game blueprint accepts. It says so, and lets the analyst accept the event
 * as an exception, which takes it out of the Odd cases review.
 *
 * Renders nothing for an event that is not an odd case.
 */
export const EventOddCaseStatusFooter: FC<EventOddCaseStatusFooterProps> = ({ gameBlueprint, eventTypeId, blueprintFamilies, accepted, onAcceptedChange }) => {
  const { t } = useTranslation()
  const userLanguage = useLiveEventsTrackingUserLanguage()

  if (!gameBlueprint || !eventTypeId || gameBlueprintSelectableForEventType(gameBlueprint, eventTypeId, blueprintFamilies)) return null

  const blueprintFamily = blueprintFamilies?.find((candidate) => candidate.id === gameBlueprint.blueprintFamilyId)
  const blueprintFamilyName = blueprintFamily?.names[userLanguage] || blueprintFamily?.names.en
  const eventTypeName = languageServiceNameResolver.eventTypeName(eventTypeId) || eventTypeId

  const title = accepted ? t('internal-live-events:accepted_odd_case_footer_title') : t('internal-live-events:odd_case_footer_title')
  const text = accepted
    ? t('internal-live-events:accepted_odd_case_footer_text')
    : blueprintFamilyName
    ? t('internal-live-events:odd_case_footer_text', { blueprintFamily: blueprintFamilyName, eventType: eventTypeName })
    : t('internal-live-events:odd_case_footer_text_unnamed_family', { eventType: eventTypeName })

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        p: '16px 26px',
        borderTop: (theme) => `1px solid ${ruleColor(theme)}`,
        backgroundColor: accepted ? acceptedBackground : oddCaseBackground,
        transition: 'background-color 150ms',
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: accepted ? acceptedBadgeBackground : oddCaseBadgeBackground,
        }}
      >
        {accepted ? <Check sx={{ fontSize: 16, color: 'success.dark' }} /> : <ReportProblemOutlined sx={{ fontSize: 17, color: 'warning.main' }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '14.5px', fontWeight: 'bold' }} color="text.primary">
          {title}
        </Typography>
        <Typography sx={{ fontSize: '12.5px', lineHeight: 1.45, color: mutedText, textWrap: 'pretty' }}>{text}</Typography>
      </Box>
      <FormControlLabel
        labelPlacement="start"
        control={<Switch checked={accepted} onChange={(event) => onAcceptedChange(event.target.checked)} color="primary" />}
        label={<Typography sx={{ fontSize: '13px', fontWeight: 'bold' }}>{t('internal-live-events:accept_as_odd_case')}</Typography>}
        sx={{ margin: 0, gap: 1, flexShrink: 0 }}
      />
    </Box>
  )
}
